import { useEffect, useRef } from "react";
import type { Shipment } from "api";
import { ComingSoonPane } from "components/ComingSoon";
import { NAV_ITEMS } from "components/Nav/nav.model";
import { ShipmentDetail } from "components/ShipmentDetail";
import type { LoadStatus } from "hooks/useProfile";
import type { ShipmentRoute } from "utils/route.utils";
import { MyShipmentsPanel } from "./MyShipmentsPanel";
import { TrackingPanel } from "./TrackingPanel";

const SECTION_LABEL = new Map(
  (NAV_ITEMS.find((item) => item.key === "shipments")?.sub ?? []).map((sub) => [sub.key, sub.label])
);

type ShipmentsPaneProps = {
  tab: string;
  /** The shipment whose page is open over My Shipments, or null for the list. */
  openShipment: ShipmentRoute | null;
  shipments: Shipment[];
  status: LoadStatus;
  onRemove: (ids: number[]) => Promise<void>;
  onAdd: (shipment: Shipment) => void;
  onOpenShipment: (id: number) => void;
  onSelectShipmentTab: (tab: string) => void;
  onCloseShipment: () => void;
  onSessionLost: () => void;
  onChoosePlan: () => void;
  /** Plan & Billing, from the out-of-credits prompt. */
  onBuyCredits: () => void;
};

/**
 * My Shipments and Tracking have pages; the rest are hidden from the menu and
 * from the router (see `nav.model`). This fallback is what the first
 * of them renders on the day its flag comes off and before its page exists —
 * it names the section rather than showing an empty card.
 */
export const ShipmentsPane = ({
  tab,
  openShipment,
  shipments,
  status,
  onRemove,
  onAdd,
  onOpenShipment,
  onSelectShipmentTab,
  onCloseShipment,
  onSessionLost,
  onChoosePlan,
  onBuyCredits,
}: ShipmentsPaneProps) => {
  /** Where the list was scrolled when a shipment opened over it. */
  const listTop = useRef(0);
  const detailOpen = openShipment !== null;
  const wasOpen = useRef(detailOpen);

  // Back to the list — breadcrumb or browser Back — lands on the row the
  // visitor left from. Two frames, as in the design: the list has to lay out
  // again before the page is tall enough to scroll that far.
  useEffect(() => {
    const closed = wasOpen.current && !detailOpen;

    wasOpen.current = detailOpen;
    if (!closed) return;

    const top = listTop.current;

    requestAnimationFrame(() => {
      window.scrollTo(0, top);
      requestAnimationFrame(() => window.scrollTo(0, top));
    });
  }, [detailOpen]);

  if (tab === "shipments") {
    return (
      <div className="pc-pane">
        {/* Kept mounted behind the page so its filters survive the round trip. */}
        <div hidden={detailOpen}>
          <MyShipmentsPanel
            shipments={shipments}
            status={status}
            onRemove={onRemove}
            onAdd={onAdd}
            onOpenShipment={(id) => {
              listTop.current = window.scrollY;
              onOpenShipment(id);
            }}
            onSessionLost={onSessionLost}
            onChoosePlan={onChoosePlan}
            onBuyCredits={onBuyCredits}
          />
        </div>

        {openShipment !== null && (
          <ShipmentDetail
            key={openShipment.id}
            id={openShipment.id}
            tab={openShipment.tab}
            onSelectTab={onSelectShipmentTab}
            onBack={onCloseShipment}
            onSessionLost={onSessionLost}
          />
        )}
      </div>
    );
  }

  if (tab === "tracking") {
    return (
      <div className="pc-pane">
        <TrackingPanel />
      </div>
    );
  }

  const label = SECTION_LABEL.get(tab) ?? "Shipments";

  return (
    <ComingSoonPane
      title={label}
      text={`${label} is on its way. In the meantime your usage is under My Qubics and your keys under API Keys.`}
    />
  );
};
